/**
 * Widget V2 — Coaches index (discovery)
 *
 * Browse the club's coaches. Cards link to a profile that funnels into a
 * lessons booking pre-filtered to that coach. Data comes entirely from the
 * init payload (`trainers`), so this is frontend-only.
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CardActionArea from "@mui/material/CardActionArea";
import Chip from "@mui/material/Chip";

import { useWidget } from "/imports/state/WidgetContext";
import { EmptyState } from "/imports/ui/components/shared";
import { CoachAvatar, fullName, PageHeading, DISCOVERY_CARD_SX } from "/imports/ui/components/discovery";

function CoachesView() {
  const navigate = useNavigate();
  const { slug, trainers, locations } = useWidget();

  const [locationFilter, setLocationFilter] = useState(null);

  const activeCoaches = useMemo(() => (trainers || []).filter((t) => t.isActive !== false), [trainers]);

  const filtered = useMemo(() => {
    if (!locationFilter) return activeCoaches;
    return activeCoaches.filter((t) => (t.locationIds || []).includes(locationFilter));
  }, [activeCoaches, locationFilter]);

  const locationName = (id) => (locations || []).find((l) => l._id === id)?.name;

  return (
    <Box>
      <PageHeading title="Our Coaches" subtitle="Meet the team and book directly with your coach." onBack={() => navigate(`/${slug}`)} />

      {(locations || []).length > 1 && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
          <Chip
            label="All locations"
            onClick={() => setLocationFilter(null)}
            color={!locationFilter ? "primary" : "default"}
            variant={!locationFilter ? "filled" : "outlined"}
          />
          {locations.map((l) => (
            <Chip
              key={l._id}
              label={l.name}
              onClick={() => setLocationFilter(l._id)}
              color={locationFilter === l._id ? "primary" : "default"}
              variant={locationFilter === l._id ? "filled" : "outlined"}
            />
          ))}
        </Box>
      )}

      {filtered.length === 0 ? (
        <EmptyState title="No coaches to show" description="There are no coaches listed for this selection yet." />
      ) : (
        <Grid container spacing={2}>
          {filtered.map((coach) => (
            <Grid item xs={12} sm={6} md={4} key={coach._id}>
              <Box sx={{ ...DISCOVERY_CARD_SX, overflow: "hidden", height: "100%" }}>
                <CardActionArea
                  onClick={() => navigate(`/${slug}/coaches/${coach._id}`)}
                  sx={{ p: 2.5, height: "100%", display: "flex", alignItems: "center", gap: 2, justifyContent: "flex-start" }}
                >
                  <CoachAvatar trainer={coach} size={56} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>
                      {fullName(coach)}
                    </Typography>
                    {coach.title && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {coach.title}
                      </Typography>
                    )}
                    {(coach.locationIds || []).length > 0 && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mt: 0.25 }}>
                        {coach.locationIds.map(locationName).filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                  </Box>
                </CardActionArea>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default CoachesView;
